import AppConfig from 'config'
import { useFocusEffect } from 'expo-router'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import { useCallback } from 'react'
import { View } from 'react-native'
import Segmented from '@ui/elements/Segmented/Segmented'
import Button from '@ui/buttons/Button'
import {
  useDebugPanelStore,
  useRegisteredDebugModals,
} from './useDebugPanelStore'
import ModalWide from '@ui/components/Modal/variants/ModalWide'

interface Props {
  debugKey: string
  label: string
  children: React.ReactNode
}

function DebugModalInner({ debugKey, label, children }: Props) {
  const register = useDebugPanelStore((s) => s.register)
  const unregister = useDebugPanelStore((s) => s.unregister)
  const openKey = useDebugPanelStore((s) => s.openKey)
  const minimizedKey = useDebugPanelStore((s) => s.minimizedKey)
  const open = useDebugPanelStore((s) => s.open)
  const close = useDebugPanelStore((s) => s.close)
  const minimize = useDebugPanelStore((s) => s.minimize)
  const restore = useDebugPanelStore((s) => s.restore)
  const isFocused = useIsFocused()
  const registeredModals = useRegisteredDebugModals()

  useFocusEffect(
    useCallback(() => {
      register(debugKey, label)
      return () => unregister(debugKey)
    }, [debugKey, label, register, unregister])
  )

  if (!isFocused) return null

  const tabs = registeredModals.map((m) => ({ id: m.key, label: m.label }))
  const activeTab = tabs.find((t) => t.id === openKey)
  const isMinimized = minimizedKey === debugKey

  return (
    <>
      <ModalWide
        open={openKey === debugKey}
        close={() => openKey === debugKey && minimize()}
      >
        <View className="flex w-full flex-row items-center justify-between px-4 pt-4">
          <Segmented
            options={tabs}
            value={activeTab}
            onChange={(opt) => open(opt.id)}
          />
          <View className="flex flex-row items-center gap-2">
            <Button
              variant="outlined"
              size="xs"
              icon="start"
              iconStart="minus"
              onPress={minimize}
            />
            <Button
              variant="outlined"
              size="xs"
              icon="start"
              iconStart="x"
              onPress={() => openKey === debugKey && close()}
            />
          </View>
        </View>
        {children}
      </ModalWide>

      {isMinimized && (
        <View
          className="absolute bottom-0 left-0 right-0"
          pointerEvents="box-none"
          style={{ zIndex: 9999 }}
        >
          <View className="flex items-start pb-8 pl-6">
            <Button
              variant="outlined"
              size="xs"
              icon="both"
              iconStart="bug"
              iconEnd="arrows-diagonal"
              label={label}
              onPress={restore}
            />
          </View>
        </View>
      )}
    </>
  )
}

export default function DebugModal(props: Props) {
  if (!AppConfig.isDev) return null
  return <DebugModalInner {...props} />
}
